import { registerAuditLabels } from "@vixio/ui";
import { useQuery } from "@tanstack/react-query";
import { from, unwrap, type FileLink } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo docs (ver modules/docs/README.md). */

export type DocStatus = "valid" | "revoked" | "superseded";

export const DOC_STATUS: StatusMap = {
  valid: { label: "válido", tone: "ok" },
  revoked: { label: "revogado", tone: "crit" },
  superseded: { label: "substituído", tone: "warn" },
};

export const CODE_RE = /^\d{4}-DOC-[0-9A-Z]{8}$/;

export interface IssuedDocument {
  id: string;
  org_id: string;
  type: string;
  type_label: string;
  title: string;
  origin_type: string | null;
  origin_id: string | null;
  code: string;
  content_sha256: string;
  storage_path: string | null;
  status: DocStatus;
  superseded_by: string | null;
  superseded_by_code: string | null;
  issued_at: string;
  issued_by: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  public: Record<string, unknown> | null;
}

export interface DocumentType {
  code: string;
  label: string;
  active: boolean;
}

export type VerifyResult =
  | { valid: false; status: "not_found" }
  | {
      valid: boolean;
      status: DocStatus;
      code: string;
      type: string;
      type_label: string;
      title: string;
      issued_at: string;
      revoked_at: string | null;
      superseded_by_code: string | null;
      org_name: string;
      public: Record<string, unknown> | null;
      sha256_prefix: string;
    };

export type FileVariant = "original" | "assinado";
export type RenderJobStatus = "queued" | "processing" | "done" | "failed" | "canceled";

export const RENDER_STATUS: StatusMap = {
  queued: { label: "na fila", tone: "info" },
  processing: { label: "gerando", tone: "info" },
  done: { label: "pronto", tone: "ok" },
  failed: { label: "falhou", tone: "crit" },
  canceled: { label: "cancelado", tone: "warn" },
};

export const VARIANT_LABEL: Record<FileVariant, string> = { original: "Original", assinado: "Assinado (com carimbo e evidências)" };

/** Linha de docs.v_render_jobs (fila de PDF). */
export interface RenderJob {
  id: string;
  document_id: string;
  code: string;
  variant: FileVariant;
  status: RenderJobStatus;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  last_error: string | null;
  done_at: string | null;
  updated_at: string;
}

/** Linha de docs.v_document_files (arquivo guardado, por variante). */
export interface DocumentFile {
  id: string;
  document_id: string;
  variant: FileVariant;
  storage_path: string;
  sha256: string;
  size_bytes: number | null;
  created_at: string;
}

/** Arquivos e fila de PDF de um documento; enquanto houver job pendente, atualiza sozinho. */
export function useDocumentPdf(documentId: string | null | undefined) {
  return useQuery({
    queryKey: ["docs", "pdf", documentId],
    enabled: Boolean(documentId),
    refetchInterval: (q) => ((q.state.data?.jobs ?? []).some((j) => j.status === "queued" || j.status === "processing") ? 5000 : false),
    queryFn: async () => {
      const [files, jobs] = await Promise.all([
        unwrap<DocumentFile[]>(from("docs", "v_document_files").select("id, document_id, variant, storage_path, sha256, size_bytes, created_at").eq("document_id", documentId!)),
        unwrap<RenderJob[]>(from("docs", "v_render_jobs").select("id, document_id, code, variant, status, attempts, max_attempts, next_attempt_at, last_error, done_at, updated_at").eq("document_id", documentId!)),
      ]);
      return { files, jobs };
    },
  });
}

export function useDocumentTypes() {
  return useQuery({
    queryKey: ["docs", "types"],
    staleTime: 10 * 60_000,
    queryFn: () => unwrap<DocumentType[]>(from("docs", "document_types").select("code, label, active").order("label")),
  });
}

/** SHA-256 em hexadecimal de bytes ou texto (Web Crypto). */
export async function sha256Hex(data: ArrayBuffer | string): Promise<string> {
  const bytes = typeof data === "string" ? new TextEncoder().encode(data) : new Uint8Array(data);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Normaliza o código digitado (maiúsculas, sem espaços). */
export function normalizeCode(s: string): string {
  return s.trim().toUpperCase().replace(/\s+/g, "");
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "docs.issue": {
      args: { p_org: string; p_type: string; p_title: string; p_origin_type: string | null; p_origin_id: string | null; p_sha256: string; p_snapshot: Record<string, unknown>; p_storage_path?: string | null };
      returns: { id: string; code: string; content_sha256: string; issued_at: string; created: boolean };
    };
    "docs.revoke": { args: { p_id: string; p_reason: string }; returns: null };
    "docs.attach_file": { args: { p_id: string; p_storage_path: string }; returns: null };
    "docs.supersede": { args: { p_id: string; p_new_id: string }; returns: null };
    "docs.verify": { args: { p_code: string }; returns: VerifyResult };
    "docs.verify_by_hash": { args: { p_sha256: string }; returns: VerifyResult };
    "docs.request_render": { args: { p_document: string }; returns: { job_id: string; status: RenderJobStatus; variant: FileVariant } };
    "docs.retry_render_job": { args: { p_job: string }; returns: null };
    "docs.file_link": { args: { p_document: string; p_variant?: FileVariant | null }; returns: FileLink };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "document.issued": "Documento emitido",
  "document.file_attached": "Arquivo registrado",
  "document.file_downloaded": "PDF baixado",
  "document.render_requested": "PDF pedido",
  "document.render_requeued": "PDF reprocessado",
  "document.render_failed": "PDF falhou",
  "document.render_canceled": "PDF cancelado",
  "document.revoked": "Documento revogado",
  "document.superseded": "Documento substituído",
  "document.public_verified": "Verificado publicamente",
  "document.public_verify_miss": "Verificação sem resultado",
  "document_type.upserted": "Tipo de documento alterado",
});
